/**
 * Centralized mappings for ToneShift modifier levels
 * Maps numeric levels (-3 to +3) to database values and display names
 */

export const MODIFIER_LEVEL_MAPPINGS = {
  assertiveness: {
    levels: {
      '-3': "most_collaborative",
      '-2': "more_collaborative", 
      '-1': "collaborative",
      '1': "decisive",
      '2': "more_decisive",
      '3': "most_decisive"
    },
    display: {
      '-3': 'Most Collaborative',
      '-2': 'More Collaborative', 
      '-1': 'Collaborative',
      '1': 'Decisive',
      '2': 'More Decisive',
      '3': 'Most Decisive'
    }
  },
  length: {
    levels: {
      '-3': "most_concise",
      '-2': "more_concise",
      '-1': "concise", 
      '1': "detailed",
      '2': "more_detailed",
      '3': "most_detailed"
    },
    display: {
      '-3': 'Most Concise',
      '-2': 'More Concise',
      '-1': 'Concise', 
      '1': 'Detailed',
      '2': 'More Detailed',
      '3': 'Most Detailed'
    }
  },

  directness: {
    levels: {
      '-3': "most_diplomatic",
      '-2': "more_diplomatic",
      '-1': "diplomatic",
      '1': "blunt",
      '2': "more_blunt", 
      '3': "most_blunt"
    },
    display: {
      '-3': 'Most Diplomatic',
      '-2': 'More Diplomatic',
      '-1': 'Diplomatic',
      '1': 'Blunt',
      '2': 'More Blunt', 
      '3': 'Most Blunt'
    }
  },
  energy: {
    levels: {
      '-3': "most_calm",
      '-2': "more_calm",
      '-1': "calm",
      '1': "excited",
      '2': "more_excited",
      '3': "most_excited"
    },
    display: {
      '-3': 'Most Calm',
      '-2': 'More Calm',
      '-1': 'Calm',
      '1': 'Excited',
      '2': 'More Excited',
      '3': 'Most Excited'
    }
  },
  formality: {
    levels: {
      '-3': "most_casual",
      '-2': "more_casual", 
      '-1': "casual",
      '1': "professional",
      '2': "more_professional",
      '3': "most_professional"
    },
    display: {
      '-3': 'Most Casual',
      '-2': 'More Casual', 
      '-1': 'Casual',
      '1': 'Professional',
      '2': 'More Professional',
      '3': 'Most Professional'
    }
  },
  urgency: {
    levels: {
      '-3': "most_relaxed",
      '-2': "more_relaxed",
      '-1': "relaxed",
      '1': "immediate",
      '2': "more_immediate",
      '3': "most_immediate"
    },
    display: {
      '-3': 'Most Relaxed',
      '-2': 'More Relaxed',
      '-1': 'Relaxed',
      '1': 'Immediate',
      '2': 'More Immediate',
      '3': 'Most Immediate'
    }
  },

  humor: {
    levels: {
      '-3': "most_humorous",
      '-2': "more_humorous",
      '-1': "humorous",
      '1': "serious",
      '2': "more_serious",
      '3': "most_serious"
    },
    display: {
      '-3': 'Most Humorous',
      '-2': 'More Humorous',
      '-1': 'Humorous',
      '1': 'Serious',
      '2': 'More Serious',
      '3': 'Most Serious'
    }
  }
};

/**
 * Get the database level name for a modifier and numeric level
 * @param {string} modifierName - Name of the modifier
 * @param {number} level - Numeric level (-3 to +3)
 * @returns {string|null} Database level name or null if not found
 */
export const getModifierLevelName = (modifierName, level) => {
  const modifierMappings = MODIFIER_LEVEL_MAPPINGS[modifierName.toLowerCase()];
  const levelName = modifierMappings ? modifierMappings.levels[level.toString()] : null;
  return levelName;
};

/**
 * Get the display name for a modifier and numeric level
 * @param {string} modifierName - Name of the modifier
 * @param {number} level - Numeric level (-3 to +3)
 * @returns {string} Display name or fallback
 */
export const getModifierLevelDisplayName = (modifierName, level) => {
  const modifierMappings = MODIFIER_LEVEL_MAPPINGS[modifierName.toLowerCase()];
  const levelStr = level.toString();
  
  if (modifierMappings && modifierMappings.display[levelStr]) {
    return modifierMappings.display[levelStr];
  }
  
  // Fallback to level number if no mapping found
  return level > 0 ? `Level +${level}` : level < 0 ? `Level ${level}` : 'Neutral';
}; 